import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from './api/client';
import { useQuery } from './hooks/useApi';
import { useI18n } from './i18n';
import { Header } from './components/Header';
import { CategoryBar } from './components/CategoryBar';
import { KitsPage } from './pages/KitsPage';
import { FilterBar, type FilterState } from './components/FilterBar';
import { Banner } from './components/States';
import { SkinsPage } from './pages/SkinsPage';
import { VariantsPage } from './pages/VariantsPage';
import { ListingsPage } from './pages/ListingsPage';
import { WatchlistPage } from './pages/WatchlistPage';
import { LoginPage, SignupPage } from './pages/SignupPage';
import { ExigeLogin, useRetornoAposLogin } from './components/ExigeLogin';
import { AdminPage } from './pages/AdminPage';
import { HomePage } from './pages/HomePage';
import { SellPage } from './pages/SellPage';
import { UpgradePage } from './pages/UpgradePage';
import { CartPage } from './pages/CartPage';
import { OrderPage } from './pages/OrderPage';
import { OrdersPage } from './pages/OrdersPage';
import { FeedbacksPage } from './pages/FeedbacksPage';
import { MainNav } from './components/MainNav';
import { Background } from './components/Background';
import { useAuth } from './hooks/useAuth';
import { WeaponsProvider } from './hooks/useWeapons';
import { useSupport } from './hooks/useSupport';
import { SupportWidget } from './components/SupportWidget';
import { SiteFooter } from './components/SiteFooter';
import { BannerCookies } from './components/BannerCookies';
import { PrivacidadePage } from './pages/PrivacidadePage';
import { PonteDeRastreamento } from './lib/tracking/react';
import { trackSearch } from './lib/tracking';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const { t } = useI18n();
  const search = params.get('q') ?? '';

  // A volta da Steam cai sempre na home; isto devolve quem entrou ao endereço
  // que tentou abrir antes de esbarrar na parede de login.
  useRetornoAposLogin();

  const weapons = useQuery(() => api.weapons(), []);
  // O health só serve para o aviso de credencial expirada, que é do painel.
  // Antes ele era relido a cada navegação, para todo mundo: uma chamada por
  // clique no site inteiro para um banner que o cliente nunca vê. Agora roda
  // só para o admin, e uma vez — `enabled` já está nas deps do useQuery.
  const health = useQuery(() => api.health(), [], isAdmin);

  const { weaponCode, kind } = useRouteSelection();

  // A linha "Categoria" acompanha o tipo da arma que está sendo vista.
  const activeKind = useMemo(() => {
    if (kind) return kind;
    if (!weaponCode || !weapons.data) return null;
    return weapons.data.groups.find((g) => g.items.some((i) => i.code === weaponCode))?.kind ?? null;
  }, [kind, weaponCode, weapons.data]);

  // O título da aba usa o nome traduzido do tipo, não a chave crua.
  const kindLabel = useMemo(() => {
    if (!kind) return null;
    const found = weapons.data?.kinds.find((k) => k.key === kind);
    return found ? found.pt : kind;
  }, [kind, weapons.data]);

  const weaponLabel = useMemo(() => {
    if (!weaponCode) return null;
    for (const group of weapons.data?.groups ?? []) {
      const found = group.items.find((i) => i.code === weaponCode);
      if (found) return found.name;
    }
    return weaponCode;
  }, [weaponCode, weapons.data]);

  useEffect(() => {
    const label = weaponLabel ?? kindLabel;
    document.title = label ? `${label} · ${t('app.name')}` : t('app.name');
  }, [weaponLabel, kindLabel, t]);

  // As facetas que não são arma/categoria moram na query string.
  const csv = (name: string) => (params.get(name) ?? '').split(',').filter(Boolean);
  const num = (name: string) => {
    const raw = params.get(name);
    if (raw === null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const filterState: FilterState = {
    kind: activeKind,
    weapon: weaponCode,
    exteriors: csv('exterior'),
    rarities: csv('rarity'),
    qualities: csv('quality'),
    statTrak: params.get('st') ?? 'all',
    minPrice: num('minPrice'),
    maxPrice: num('maxPrice'),
    teams: csv('team'),
    colors: csv('color'),
  };

  function applyFilters(next: Partial<FilterState>) {
    const merged = new URLSearchParams(params);
    const setList = (name: string, list: string[] | undefined) => {
      if (list === undefined) return;
      if (list.length === 0) merged.delete(name);
      else merged.set(name, list.join(','));
    };
    setList('exterior', next.exteriors);
    setList('rarity', next.rarities);
    setList('quality', next.qualities);
    setList('team', next.teams);
    setList('color', next.colors);

    const setValue = (name: string, value: string | number | null | undefined, omit?: string) => {
      if (value === undefined) return;
      if (value === null || value === omit) merged.delete(name);
      else merged.set(name, String(value));
    };
    setValue('st', next.statTrak, 'all');
    if ('minPrice' in next) setValue('minPrice', next.minPrice);
    if ('maxPrice' in next) setValue('maxPrice', next.maxPrice);

    merged.delete('page');

    // Mudar de arma ou categoria troca a rota; as facetas seguem junto.
    const qs = merged.toString();
    const suffix = qs ? `?${qs}` : '';

    if ('weapon' in next || 'kind' in next) {
      const weapon = 'weapon' in next ? next.weapon : weaponCode;
      const nextKind = 'kind' in next ? next.kind : activeKind;
      if (weapon) navigate(`/w/${weapon}${suffix}`);
      else if (nextKind) navigate(`/k/${nextKind}${suffix}`);
      else navigate(`/all${suffix}`);
      return;
    }
    setParams(merged, { replace: true });
  }

  function clearFilters() {
    // Limpa as facetas, mas mantém onde o usuário está navegando.
    const base = weaponCode ? `/w/${weaponCode}` : activeKind ? `/k/${activeKind}` : '/all';
    navigate(base);
  }

  /** A busca vive na query string, para o link continuar compartilhável. */
  function setSearch(value: string) {
    trackSearch(value);
    const merged = new URLSearchParams(params);
    if (value) merged.set('q', value);
    else merged.delete('q');
    merged.delete('page');
    setParams(merged, { replace: true });
  }

  // O catálogo mora nestas rotas; home, venda, upgrade e painel não usam
  // categorias nem filtros.
  //
  // Vale para o visitante também: a vitrine é aberta sem login, e sem as
  // categorias e os filtros ele via uma grade solta, sem como navegar. A
  // exceção são os desgastes e anúncios (/s/ e /t/), que pedem login: ali o
  // visitante vê a parede, e filtros em cima dela pareceriam erro de carregamento.
  //
  // "/kits" não colide com o prefixo "/k/": o caractere seguinte é "i", não "/".
  const naVitrine =
    location.pathname === '/all' || ['/w/', '/c/', '/k/'].some((r) => location.pathname.startsWith(r));
  const naParede = !user && /\/(s|t)\//.test(location.pathname);
  const noCatalogo = naVitrine && !naParede;

  /**
   * A barra de categorias vale também na vitrine de kits.
   *
   * Ela é navegação, não filtro: sumir com ela em /kits deixava o cliente sem
   * caminho de volta para o catálogo, numa página que é justamente a que ele
   * abre por curiosidade. Os filtros ficam de fora — ali não há o que filtrar,
   * e só comeriam a altura útil.
   */
  const comCategorias = noCatalogo || location.pathname.startsWith('/kits');

  return (
    <WeaponsProvider data={weapons.data}>
      <PonteDeRastreamento />
      <Background />
      <div className="app">
        <div className="appbar">
          <Header />
          <MainNav />
          {/* Categorias e filtros são do catálogo. Nas telas de inventário eles
              não filtram nada e só ocupariam a altura útil da página. */}
          {comCategorias && (
          <CategoryBar
            data={weapons.data}
            activeKind={activeKind}
            activeWeapon={weaponCode}
            activeTeams={filterState.teams}
            onSelectKind={(kind) => applyFilters({ kind, weapon: null, teams: [] })}
            onSelectWeapon={(code) => applyFilters({ weapon: code })}
            onSelectTeam={(teams) => applyFilters({ kind: 'agent', weapon: null, teams })}
          />
          )}
          {noCatalogo && (
          <FilterBar
            data={weapons.data}
            state={filterState}
            onChange={applyFilters}
            onClear={clearFilters}
            search={search}
            onSearch={setSearch}
          />
          )}
        </div>

        <div className="layout">
          <main className="content">
            {weapons.error &&
              (isAdmin ? (
                <Banner kind="error">
                  {t('error.backend', { message: weapons.error.message, url: 'http://localhost:4000' })}
                </Banner>
              ) : (
                <Banner kind="warn">
                  Estamos com uma instabilidade no carregamento das skins. Tente de novo em instantes.
                </Banner>
              ))}

            {isAdmin && health.data?.youpin.credentialsExpiredSince && (
              <Banner kind="error">
                {t('error.credentialsExpired', { date: health.data.youpin.credentialsExpiredSince })}
              </Banner>
            )}

            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/all" element={<SkinsPage />} />
              <Route path="/kits" element={<KitsPage />} />
              {/* Navegar a partir de "Todas" cai aqui: sem estas duas rotas, o
                  clique em qualquer card do catálogo geral dava 404. */}
              <Route path="/all/s/:baseKey" element={<ExigeLogin><VariantsPage /></ExigeLogin>} />
              <Route path="/all/t/:templateId" element={<ExigeLogin><ListingsPage /></ExigeLogin>} />
              <Route path="/w/:weaponCode" element={<SkinsPage />} />
              <Route path="/c/:category" element={<SkinsPage />} />
              <Route path="/k/:kind" element={<SkinsPage />} />
              <Route path="/k/:kind/s/:baseKey" element={<ExigeLogin><VariantsPage /></ExigeLogin>} />
              <Route path="/k/:kind/t/:templateId" element={<ExigeLogin><ListingsPage /></ExigeLogin>} />
              <Route path="/w/:weaponCode/s/:baseKey" element={<ExigeLogin><VariantsPage /></ExigeLogin>} />
              <Route path="/c/:category/s/:baseKey" element={<ExigeLogin><VariantsPage /></ExigeLogin>} />
              <Route path="/w/:weaponCode/t/:templateId" element={<ExigeLogin><ListingsPage /></ExigeLogin>} />
              <Route path="/c/:category/t/:templateId" element={<ExigeLogin><ListingsPage /></ExigeLogin>} />
              <Route path="/vender" element={<ExigeLogin><SellPage /></ExigeLogin>} />
              <Route path="/sacola" element={<ExigeLogin><CartPage /></ExigeLogin>} />
              <Route path="/pedidos" element={<ExigeLogin><OrdersPage /></ExigeLogin>} />
              {/* A tela de dúvidas virou a janela flutuante; o endereço
                  antigo continua valendo e abre a janela. */}
              <Route path="/duvidas" element={<AbrirAtendimento />} />
              <Route path="/feedbacks" element={<FeedbacksPage />} />
              <Route path="/politica-de-privacidade" element={<PrivacidadePage />} />
              <Route path="/pedido/:code" element={<ExigeLogin><OrderPage /></ExigeLogin>} />
              <Route path="/upgrade" element={<ExigeLogin><UpgradePage /></ExigeLogin>} />
              <Route path="/favoritos" element={<ExigeLogin><WatchlistPage /></ExigeLogin>} />
              <Route path="/watchlist" element={<ExigeLogin><WatchlistPage /></ExigeLogin>} />
              <Route path="/entrar" element={<LoginPage />} />
              <Route path="/cadastro" element={<SignupPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<Banner kind="warn">{t('error.pageNotFound')}</Banner>} />
            </Routes>
          </main>
        </div>

        <SiteFooter />
        <SupportWidget />
        <BannerCookies />
      </div>
    </WeaponsProvider>
  );
}

/** O endereço /duvidas abre a janela de atendimento e devolve para a home. */
function AbrirAtendimento() {
  const { abrir } = useSupport();
  useEffect(() => {
    abrir();
  }, [abrir]);
  return <Navigate to="/" replace />;
}

/** Lê a arma ou o tipo de item da rota atual, seja qual for o nível. */
function useRouteSelection(): { weaponCode: string | null; kind: string | null } {
  const location = useLocation();
  const weapon = location.pathname.match(/^\/w\/([^/]+)/);
  const kind = location.pathname.match(/^\/k\/([^/]+)/);
  return {
    weaponCode: weapon?.[1] ? decodeURIComponent(weapon[1]) : null,
    kind: kind?.[1] ? decodeURIComponent(kind[1]) : null,
  };
}
